<template>
  <div class="min-h-screen">
    <AppNavbar title="Favourite Locations" :back="true" />

    <main class="max-w-7xl mx-auto px-8 py-8">

      <!-- Page header -->
      <div class="mb-8">
        <div class="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <span>Dashboard</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
          <span class="text-slate-600 font-medium">Locations</span>
        </div>
        <div class="flex items-start justify-between gap-6">
          <div>
            <h1 class="text-3xl font-bold text-slate-800">Favourite Locations</h1>
            <p class="text-slate-500 mt-1.5 text-base">Save pickup spots you use often and get live weather for any of them.</p>
          </div>
        </div>
        <div class="mt-4 space-y-2">
          <AlertBanner :message="success" type="success" />
          <AlertBanner :message="error" type="error" />
        </div>
      </div>

      <!-- Two-column grid -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        <!-- Forms: narrower -->
        <div class="lg:col-span-2 space-y-6">
          <LocationForm @location-created="loadLocations" />

          <!-- Edit form -->
          <div v-if="editingLocation" class="bg-white border border-emerald-200 rounded-2xl shadow-sm p-6">
            <div class="flex items-center gap-2 mb-5">
              <div class="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </div>
              <h2 class="text-base font-bold text-slate-800">Edit Location</h2>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1.5">Label</label>
                <input
                  v-model="editForm.label"
                  type="text"
                  placeholder="e.g. Home"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1.5">Address</label>
                <input
                  v-model="editForm.address"
                  type="text"
                  placeholder="Street address"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1.5">City</label>
                <input
                  v-model="editForm.city"
                  type="text"
                  placeholder="City"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1.5">Country</label>
                <input
                  v-model="editForm.country"
                  type="text"
                  placeholder="Country"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>

              <div class="flex gap-3 pt-1">
                <button
                  @click="updateLocation"
                  class="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors"
                >
                  Save changes
                </button>
                <button
                  @click="cancelEditLocation"
                  class="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm py-2.5 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- List + weather: wider -->
        <div class="lg:col-span-3">
          <LocationList
            :locations="locations"
            :weather="weather"
            :loading="loading"
            @refresh="loadLocations"
            @weather="loadWeather"
            @delete="deleteLocation"
            @edit="startEditLocation"
          />
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import AppNavbar from "@/components/AppNavbar.vue";
import LocationForm from "@/components/LocationForm.vue";
import LocationList from "@/components/LocationList.vue";
import AlertBanner from "@/components/AlertBanner.vue";

export default {
  name: "LocationsView",
  components: { AppNavbar, LocationForm, LocationList, AlertBanner },
  data() {
    return {
      locations: [],
      weather: null,
      success: "",
      error: "",
      loading: false,
      editingLocation: null,
      editForm: {
        label: "",
        address: "",
        city: "",
        country: "",
      },
    };
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push("/login");
      return;
    }
    this.loadLocations();
  },
  methods: {
    async loadLocations() {
      try {
        this.loading = true;
        this.error = "";
        const response = await this.$store.dispatch("fetchLocations");
        this.locations = response.data.locations;
      } catch (error) {
        this.error = error.response?.data?.message || "Could not load locations";
      } finally {
        this.loading = false;
      }
    },

    async loadWeather(locationId) {
      try {
        this.error = "";
        const response = await this.$store.dispatch("getWeather", locationId);
        this.weather = response.data.weather;
      } catch (error) {
        this.error = error.response?.data?.message || "Could not load weather";
      }
    },

    async deleteLocation(locationId) {
      try {
        this.success = "";
        this.error = "";
        await this.$store.dispatch("deleteLocation", locationId);
        this.success = "Location removed successfully.";
        await this.loadLocations();
      } catch (error) {
        this.error = error.response?.data?.message || "Could not remove location";
      }
    },

    startEditLocation(location) {
      this.editingLocation = location;
      this.editForm = {
        label: location.label,
        address: location.address,
        city: location.city,
        country: location.country,
      };
    },

    async updateLocation() {
      try {
        this.success = "";
        this.error = "";
        await this.$store.dispatch("updateLocation", {
          locationId: this.editingLocation._id,
          locationData: this.editForm,
        });
        this.success = "Location updated successfully.";
        this.editingLocation = null;
        this.editForm = { label: "", address: "", city: "", country: "" };
        await this.loadLocations();
      } catch (error) {
        this.error = error.response?.data?.message || "Could not update location";
      }
    },

    cancelEditLocation() {
      this.editingLocation = null;
      this.editForm = { label: "", address: "", city: "", country: "" };
    },
  },
};
</script>
